<script setup lang="ts">
import type { ClassValue } from 'tailwind-variants';
import { computed, ref, useSlots } from 'vue';
import {
    DrawerClose,
    DrawerContent,
    DrawerDescription,
    DrawerOverlay,
    DrawerPortal,
    DrawerRoot,
    DrawerTitle,
    DrawerTrigger,
} from 'vaul-vue';
import { VisuallyHidden } from 'reka-ui';
import { IconX } from '@tabler/icons-vue';
import DrawerHandle from './DrawerHandle.vue';
import { drawerVariants } from './variants.ts';

type DrawerDirection = 'top' | 'bottom' | 'left' | 'right';
type DrawerSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const props = withDefaults(defineProps<{
    /**
     * Heading text shown inside the auto-rendered header.
     * If omitted (and no `#header` slot is provided) the header is not rendered.
     */
    title?: string;
    /**
     * Subtitle text rendered below the title. Wired to `aria-describedby` for free.
     */
    description?: string;
    /**
     * Renders the built-in X close button in the top-right corner of the panel.
     * @default true
     */
    closable?: boolean;
    /**
     * Side the drawer slides in from.
     * @values 'top', 'bottom', 'left', 'right'
     * @default 'right'
     */
    direction?: DrawerDirection;
    /**
     * Size preset for the panel. Maps to width on left/right drawers and height on top/bottom drawers.
     * `full` makes the drawer cover the viewport.
     * @values 'sm', 'md', 'lg', 'xl', 'full'
     * @default 'md'
     */
    size?: DrawerSize;
    /**
     * Forwarded to vaul-vue's `DrawerRoot`. When `false`, the drawer renders without
     * a focus trap or scroll lock. Rarely needed.
     * @default true
     */
    modal?: boolean;
    /**
     * When `false`, the drawer cannot be dismissed via swipe, ESC, or click-outside.
     * @default true
     */
    dismissible?: boolean;
    /**
     * Renders the drag handle bar. Set `false` for non-draggable side menus.
     * @default true
     */
    handle?: boolean;
    /**
     * Forwarded to vaul-vue. When `true`, only the handle (not the entire content) is draggable.
     * @default false
     */
    handleOnly?: boolean;
    /**
     * Whether to render the backdrop overlay behind the panel.
     * @default true
     */
    overlay?: boolean;
    /**
     * Extra classes merged onto the panel. Use to override at call sites
     * (e.g. `lg:hidden` to scope the drawer to mobile only).
     */
    contentClass?: ClassValue;
}>(), {
    title: undefined,
    description: undefined,
    closable: true,
    direction: 'right',
    size: 'md',
    modal: true,
    dismissible: true,
    handle: true,
    handleOnly: false,
    overlay: true,
    contentClass: undefined,
});

const emit = defineEmits<{
    'after:enter': [];
    'after:leave': [];
}>();

const isOpen = defineModel<boolean | undefined>('open', { default: undefined });

const slots = useSlots();

const ui = computed(() => drawerVariants({ direction: props.direction, size: props.size }));

const isExplicitClose = ref(false);

const onUpdateOpen = (value: boolean) => {
    if (value || props.dismissible || isExplicitClose.value) {
        isOpen.value = value;
    }

    isExplicitClose.value = false;
};

const onAnimationEnd = (open: boolean) => {
    if (open) {
        emit('after:enter');
    } else {
        emit('after:leave');
    }
};
</script>

<template>
    <DrawerRoot
        :open="isOpen"
        :direction="direction"
        :modal="modal"
        :dismissible="dismissible"
        :handle-only="handleOnly"
        @update:open="onUpdateOpen"
        @animation-end="onAnimationEnd"
    >
        <DrawerTrigger
            v-if="slots.trigger"
            as-child
        >
            <slot name="trigger" />
        </DrawerTrigger>
        <DrawerPortal>
            <DrawerOverlay
                v-if="overlay"
                :class="ui.overlay()"
            />
            <DrawerContent
                data-vaul-custom-container="true"
                :class="ui.content({ class: contentClass })"
            >
                <VisuallyHidden v-if="(!title && !slots.header) || !description">
                    <DrawerTitle v-if="!title && !slots.header" />
                    <DrawerDescription v-if="!description" />
                </VisuallyHidden>

                <DrawerHandle v-if="handle" />

                <header
                    v-if="title || description || slots.header"
                    :class="ui.header()"
                >
                    <div class="grow">
                        <DrawerTitle
                            v-if="title"
                            :class="ui.title()"
                        >
                            {{ title }}
                        </DrawerTitle>
                        <DrawerTitle
                            v-else-if="slots.header"
                            :class="ui.title()"
                        >
                            <slot name="header" />
                        </DrawerTitle>
                        <DrawerDescription
                            v-if="description"
                            :class="ui.description()"
                        >
                            {{ description }}
                        </DrawerDescription>
                        <slot
                            v-if="title"
                            name="header"
                        />
                    </div>
                </header>

                <div :class="ui.body()">
                    <slot />
                </div>

                <footer
                    v-if="slots.footer"
                    :class="ui.footer()"
                >
                    <slot name="footer" />
                </footer>

                <DrawerClose
                    v-if="closable"
                    :class="ui.close()"
                    aria-label="Sluiten"
                    @click="isExplicitClose = true"
                >
                    <IconX
                        class="size-5"
                        aria-hidden="true"
                    />
                </DrawerClose>
            </DrawerContent>
        </DrawerPortal>
    </DrawerRoot>
</template>
